import React, { useState, useEffect } from 'react';
import DatePickerField from '../UI/DatePickerField';
import api from '../../services/api';
import Modal from '../UI/Modal';
import Portal from '../UI/Portal';
import { toLocalISODate, parseLocalDate, getTodayInSingaporeTimezone } from '../../utils/dateUtils';

// Simple Tooltip component for contextual help
const Tooltip = ({ content, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-xl shadow-lg top-0 left-full ml-2 max-w-xs break-words whitespace-normal">
          {content}
          <div className="absolute top-2 -left-1 w-2 h-2 bg-gray-900 rotate-45 rounded"></div>
        </div>
      )}
    </div>
  );
};

// Help content for different fields
const getFieldHelp = (category, fieldName) => {
  const helpContent = {
    departments: {
      department_name: "The official name of the department as it appears in organizational charts and reports.",
      head_of_department: "Select the staff member who leads this department. They will have approval rights for department-related requests.",
      description: "Optional description explaining the department's role and responsibilities."
    },
    locations: {
      location_name: "The name of the work location (e.g., 'Head Office', 'Workshop A').",
      address: "Full physical address including postal code for official correspondence.",
      timezone: "The timezone for this location. Affects attendance tracking and scheduling."
    },
    publicHolidays: {
      holiday_name: "The official name of the holiday as recognized by the government.",
      holiday_date: "The date the holiday falls on. Use the actual date even if observed on a different day.",
      holiday_type: "Category of holiday - National holidays are mandatory, others may be optional.",
      holiday_is_gazetted: "Whether this holiday is officially gazetted by the government.",
      holiday_affects_payroll: "Whether employees are paid for this holiday even if they don't work."
    },
    leaveTypes: {
      leave_type_name: "The name of the leave type (e.g., 'Annual Leave', 'Sick Leave').",
      leave_type_description: "Optional description explaining the purpose and usage of this leave type.",
      leave_type_statutory_days: "The number of days per year employees are entitled to by law (e.g., 14 days for annual leave).",
      leave_type_max_consecutive_days: "The maximum number of consecutive days an employee can take this leave type (e.g., 10 days). Leave empty for no limit.",
      leave_type_requires_medical_cert: "Whether a medical certificate is required for this type of leave.",
      leave_type_advance_notice_days: "Minimum number of days notice required before taking this leave.",
      leave_type_trigger_event: "Document or event that triggers eligibility for this leave type (e.g., 'Birth Certificate', 'NS Call-up Order').",
      leave_type_trigger_deadline_months: "Months after the trigger event when the leave must be consumed.",
      leave_type_is_mom_statutory: "Whether this leave type is statutorily required by the Singapore Ministry of Manpower (MOM).",
      leave_type_mom_reimbursement: "Whether the government provides reimbursement for this leave type.",
      leave_type_reimbursement_cap_per_week: "Weekly reimbursement cap from government (e.g., S$2,500/week for GPML/GPPL). Singapore government reimburses on weekly basis regardless of employee work schedule.",
      leave_type_total_reimbursement_cap: "Total reimbursement cap for entire leave scheme (e.g., S$40,000 for GPML per child). This is the maximum amount government will reimburse for the entire program period.",
      leave_type_reimbursement_cap_period_weeks: "Number of weeks the reimbursement cap applies to (e.g., 16 weeks for GPML).",
      leave_type_cap_is_per_child: "Whether program cap is per child (for maternity/paternity/adoption leave). Check this for schemes where each child gets a separate cap allocation.",
      leave_type_citizen_only: "Whether this leave type is only available to Singapore citizens."
    },
    leaveTypes: {
      employment_classification: "Which employment classification this leave type applies to. Use 'All Classifications' for universal policies like sick leave, or specific classifications for different entitlements like Annual Leave.",
      entitlement_days: "Base number of days entitled per year for this employment classification. Leave empty to use statutory days.",
      maximum_entitlement_days: "Maximum number of days an employee can accumulate regardless of years of service (e.g., 18 days cap for Staff Annual Leave). Leave blank for no cap.",
      annual_accrual_days: "Number of additional leave days added each work anniversary. For example: 1 means employees get +1 day after each year of service (Year 1: 14 days, Year 2: 15 days, etc.). Use 0 for non-accruing leave types.",
      statutory_minimum_days: "Minimum days required by MOM regulations for compliance tracking.",
      calculation_method: "Choose how leave entitlements are calculated: Accrual (earned gradually over time, MOM compliant) or Front Loaded (full entitlement given at start of year).",
      carry_forward_allowed: "Whether unused leave can be carried forward to the next year for this employment classification.",
      carry_forward_max_days: "Maximum number of days that can be carried forward (only applies if carry forward is allowed).",
      leave_type_carry_forward_allowed: "Whether unused leave can be carried to the next year.",
      leave_type_carry_forward_max_days: "Maximum number of days that can be carried forward to the next year."
    },
    foreignWorkerLevies: {
      pass_type: "The type of work pass - different passes have different levy rates.",
      sector: "The sector/industry where the worker is employed - affects levy rates.",
      levy_rate_monthly: "The monthly levy amount in Singapore Dollars that employers must pay.",
      effective_from: "The date from which this levy rate takes effect.",
      effective_to: "The date when this levy rate expires (leave blank if ongoing)."
    },
    workScheduleTemplates: {
      name: "The name for this schedule template (e.g., 'Staff 5.5-Day Schedule', 'Worker 6-Day Schedule').",
      schedule_type: "Select the work week pattern: 5-day (Mon-Fri), 5.5-day (Mon-Sat half), 6-day (Mon-Sat), or custom.",
      description: "Optional description explaining the purpose and usage of this work schedule template.",
      start_time: "Default start time for working days (e.g., 08:30 for 8:30 AM).",
      end_time: "Default end time for working days (e.g., 17:30 for 5:30 PM).",
      lunch_break_start: "Lunch break start time (e.g., 12:00 for 12:00 PM).",
      lunch_break_end: "Lunch break end time (e.g., 13:00 for 1:00 PM).",
      monday_enabled: "Enable Monday as a working day for this schedule.",
      tuesday_enabled: "Enable Tuesday as a working day for this schedule.",
      wednesday_enabled: "Enable Wednesday as a working day for this schedule.",
      thursday_enabled: "Enable Thursday as a working day for this schedule.",
      friday_enabled: "Enable Friday as a working day for this schedule.",
      saturday_enabled: "Enable Saturday as a working day for this schedule.",
      sunday_enabled: "Enable Sunday as a working day for this schedule.",
      saturday_half_day: "Enable this for 5.5-day schedules where Saturday is only a half day.",
      saturday_end_time: "Saturday end time if it's a half day (e.g., 12:30 for 12:30 PM).",
      employment_classification: "Which employment classification this template applies to (e.g., Staff, Worker, Executive Director).",
      is_default_for_classification: "Mark as primary default schedule for this employment classification.",
      priority: "Priority order for multiple templates per classification (lower number = higher priority).",
      max_daily_work_hours: "Maximum total work hours per day including overtime (e.g., 12.00 hours).",
      max_monthly_overtime_hours: "Maximum overtime hours allowed per month (e.g., 72.00 hours).",
      day_reset_time: "Time when the work day resets (e.g., 00:00 for midnight, 04:00 for 4 AM).",
      enforce_daily_limits: "Whether to enforce the daily work hour limits for compliance.",
      enforce_monthly_limits: "Whether to enforce the monthly overtime limits for compliance.",
      is_active: "Whether this template is currently active and available for use."
    }
    // Add more help content as needed
  };
  
  return helpContent[category]?.[fieldName] || "No help available for this field.";
};

const ConfigForm = ({ 
  category, 
  item = null, 
  isOpen, 
  onClose, 
  onSave, 
  title = "Configuration",
  configData = {},
}) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [staffUsers, setStaffUsers] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState(new Set());

  // Form field configurations for different categories
  const getFormFields = (category) => {
    const fieldConfigs = {
      departments: [
        { name: 'department_name', label: 'Department Name', type: 'text', required: true, help: 'Enter the official name of the department (e.g., Human Resources, Engineering)' },
        { name: 'description', label: 'Description', type: 'textarea', required: false, help: 'Optional description of the department\'s purpose and responsibilities' }
      ],
      locations: [
        { name: 'location_name', label: 'Location Name', type: 'text', required: true },
        { name: 'address', label: 'Address', type: 'textarea', required: true },
        { name: 'timezone', label: 'Timezone', type: 'select', required: true, options: [
          { value: 'Asia/Singapore', label: 'Asia/Singapore' },
          { value: 'Asia/Kuala_Lumpur', label: 'Asia/Kuala Lumpur' },
          { value: 'Asia/Bangkok', label: 'Asia/Bangkok' },
          { value: 'Asia/Jakarta', label: 'Asia/Jakarta' }
        ]}
      ],
      classifications: [
        { name: 'name', label: 'Classification Name', type: 'text', required: true },
        { name: 'description', label: 'Description', type: 'textarea', required: false }
      ],
      designations: [
        { name: 'name', label: 'Designation Name', type: 'text', required: true },
        { name: 'description', label: 'Description', type: 'textarea', required: false }
      ],
      employmentTypes: [
        { name: 'name', label: 'Employment Type', type: 'text', required: true },
        { name: 'description', label: 'Description', type: 'textarea', required: false }
      ],
      payGrades: [
        { name: 'grade_name', label: 'Grade Name', type: 'text', required: true },
        { name: 'salary_min', label: 'Minimum Salary', type: 'number', required: true, step: '0.01' },
        { name: 'salary_max', label: 'Maximum Salary', type: 'number', required: true, step: '0.01' }
      ],
      workScheduleTemplates: [
        { name: 'name', label: 'Template Name', type: 'text', required: true, help: 'Name for this schedule template (e.g., "Staff 5.5-Day Schedule")' },
        { name: 'schedule_type', label: 'Schedule Type', type: 'select', required: true, options: [
          { value: '5_day', label: '5-Day Work Week' },
          { value: '5_5_day', label: '5.5-Day Work Week' },
          { value: '6_day', label: '6-Day Work Week' },
          { value: 'custom', label: 'Custom Schedule' }
        ], help: 'Select the work week pattern for this template' },
        { name: 'description', label: 'Description', type: 'textarea', required: false, help: 'Optional description of this work schedule' },
        
        // Working Hours
        { name: 'start_time', label: 'Start Time', type: 'time', required: true, help: 'Default start time for working days' },
        { name: 'end_time', label: 'End Time', type: 'time', required: true, help: 'Default end time for working days' },
        { name: 'lunch_break_start', label: 'Lunch Break Start', type: 'time', required: true, help: 'Lunch break start time' },
        { name: 'lunch_break_end', label: 'Lunch Break End', type: 'time', required: true, help: 'Lunch break end time' },
        
        // Working Days
        { name: 'monday_enabled', label: 'Monday', type: 'checkbox', required: false, help: 'Enable Monday as a working day' },
        { name: 'tuesday_enabled', label: 'Tuesday', type: 'checkbox', required: false, help: 'Enable Tuesday as a working day' },
        { name: 'wednesday_enabled', label: 'Wednesday', type: 'checkbox', required: false, help: 'Enable Wednesday as a working day' },
        { name: 'thursday_enabled', label: 'Thursday', type: 'checkbox', required: false, help: 'Enable Thursday as a working day' },
        { name: 'friday_enabled', label: 'Friday', type: 'checkbox', required: false, help: 'Enable Friday as a working day' },
        { name: 'saturday_enabled', label: 'Saturday', type: 'checkbox', required: false, help: 'Enable Saturday as a working day' },
        { name: 'sunday_enabled', label: 'Sunday', type: 'checkbox', required: false, help: 'Enable Sunday as a working day' },
        
        // Saturday Options
        { name: 'saturday_half_day', label: 'Saturday Half Day', type: 'checkbox', required: false, help: 'Saturday is half-day (for 5.5-day schedule)' },
        { name: 'saturday_end_time', label: 'Saturday End Time', type: 'time', required: false, help: 'Saturday end time if half-day' },
        
        // Employment Classification
        { name: 'employment_classification', label: 'Employment Classification', type: 'select', required: false, options: [
          { value: 'executive_director', label: 'Executive Director' },
          { value: 'staff', label: 'Staff' },
          { value: 'worker', label: 'Worker' },
          { value: 'system', label: 'System Account' }
        ], help: 'Default schedule template for this employment classification' },
        { name: 'is_default_for_classification', label: 'Default for Classification', type: 'checkbox', required: false, help: 'Primary default schedule for this employment classification' },
        { name: 'priority', label: 'Priority', type: 'number', required: false, min: 1, help: 'Priority order for templates within same classification (lower = higher priority)' },
        
        // Compliance Guardrails
        { name: 'max_daily_work_hours', label: 'Max Daily Work Hours', type: 'number', required: false, min: 0, step: '0.01', help: 'Maximum total work hours per day (including overtime)' },
        { name: 'max_monthly_overtime_hours', label: 'Max Monthly Overtime Hours', type: 'number', required: false, min: 0, step: '0.01', help: 'Maximum overtime hours per month' },
        { name: 'day_reset_time', label: 'Day Reset Time', type: 'time', required: false, help: 'Time when work day resets (default: 12:00 AM)' },
        { name: 'enforce_daily_limits', label: 'Enforce Daily Limits', type: 'checkbox', required: false, help: 'Enforce daily work hour limits' },
        { name: 'enforce_monthly_limits', label: 'Enforce Monthly Limits', type: 'checkbox', required: false, help: 'Enforce monthly overtime limits' },
        { name: 'late_arrival_grace_period_minutes', label: 'Late Arrival Grace Period (minutes)', type: 'number', required: false, min: 0, max: 60, help: 'Minutes of grace period before marking attendance as late (0-60)' },
        
        // Metadata
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false, help: 'Whether this template is currently active' }
      ],
      
      leaveTypes: [
        // Main Form - Essential Fields
        { name: 'leave_type_name', label: 'Leave Type Name', type: 'text', required: true, section: 'main' },
        { name: 'employment_classification', label: 'Employment Classification', type: 'select', required: true, options: [
          { value: 'all', label: 'All Classifications (Universal)' },
          { value: 'managing_director', label: 'Managing Director' },
          { value: 'executive_director', label: 'Executive Director' },
          { value: 'staff', label: 'Staff' },
          { value: 'worker', label: 'Worker' },
          { value: 'intern', label: 'Intern' },
          { value: 'system', label: 'System Account' }
        ], help: 'Which employment classification this leave type applies to. Use "All Classifications" for universal policies like sick leave.', section: 'main' },
        { name: 'leave_type_description', label: 'Description', type: 'textarea', required: false, section: 'main' },
        { name: 'leave_type_statutory_days', label: 'Statutory Days', type: 'number', required: true, min: 0, help: 'MOM statutory minimum days per year', section: 'main' },
        { name: 'leave_type_is_paid', label: 'Is Paid Leave', type: 'checkbox', required: false, section: 'main' },
        { name: 'leave_type_requires_medical_cert', label: 'Requires Medical Certificate', type: 'checkbox', required: false, section: 'main' },
        { name: 'leave_type_advance_notice_days', label: 'Advance Notice Days', type: 'number', required: false, min: 0, help: 'Minimum advance notice required in days', section: 'main' },
        { name: 'leave_type_is_mom_statutory', label: '🇸🇬 MOM Statutory', type: 'checkbox', required: false, help: 'Whether this leave type is statutorily required by MOM', section: 'main' },
        { name: 'leave_type_citizen_only', label: '🏛️ Citizens Only', type: 'checkbox', required: false, help: 'Whether this leave type is only for Singapore citizens', section: 'main' },
        
        // Entitlement Section - NEW FIELDS
        { name: 'entitlement_days', label: 'Entitlement Days', type: 'number', required: false, min: 0, help: 'Number of days entitled per year for this classification. Leave empty to use statutory days.', section: 'entitlements' },
        { name: 'maximum_entitlement_days', label: 'Maximum Entitlement Days', type: 'number', required: false, min: 0, help: 'Maximum days that can be accrued (e.g., 18 for Staff Annual Leave)', section: 'entitlements' },
        { name: 'annual_accrual_days', label: 'Annual Accrual Days', type: 'number', required: false, min: 0, help: 'Additional days added each work anniversary (e.g., 1 day per year for Staff). Use 0 for non-accruing leave types.', section: 'entitlements' },
        { name: 'statutory_minimum_days', label: 'Statutory Minimum Days', type: 'number', required: false, min: 0, help: 'MOM statutory minimum days (for compliance tracking)', section: 'entitlements' },
        { name: 'service_months_required', label: 'Service Months Required', type: 'number', required: false, min: 0, help: 'Months of service required before eligibility (e.g., 3 months for sick leave)', section: 'entitlements' },
        
        // Policy Settings Section
        { name: 'calculation_method', label: 'Calculation Method', type: 'select', required: false, options: [
          { value: 'accrual', label: 'Accrual Method (earned over time)' },
          { value: 'front_loaded', label: 'Front Loaded (given at start of year)' }
        ], help: 'How leave entitlements are calculated and allocated', section: 'policy' },
        { name: 'carry_forward_allowed', label: 'Carry Forward Allowed', type: 'checkbox', required: false, help: 'Whether unused leave can be carried forward to next year', section: 'policy' },
        { name: 'carry_forward_max_days', label: 'Max Carry Forward Days', type: 'number', required: false, min: 0, help: 'Maximum days that can be carried forward', section: 'policy' },
        { name: 'pro_rating_method', label: 'Pro-rating Method', type: 'select', required: false, options: [
          { value: 'monthly', label: 'Monthly Pro-rating' },
          { value: 'daily', label: 'Daily Pro-rating' },
          { value: 'none', label: 'No Pro-rating' }
        ], help: 'How to calculate leave for partial years (new joiners, leavers)', section: 'policy' },
        
        // Advanced Section - Conditional Fields
        { name: 'leave_type_max_consecutive_days', label: 'Max Consecutive Days', type: 'number', required: false, min: 1, help: 'Maximum consecutive days allowed (leave empty for no limit)', section: 'advanced' },
        { name: 'leave_type_trigger_event', label: 'Trigger Event', type: 'select', required: false, options: [
          { value: 'none', label: 'No Trigger Required' },
          { value: 'birth_certificate', label: 'Birth Certificate Submission' },
          { value: 'adoption_papers', label: 'Adoption Papers' },
          { value: 'death_certificate', label: 'Death Certificate' },
          { value: 'ns_call_up', label: 'NS Call-up Order' },
          { value: 'marriage_certificate', label: 'Marriage Certificate' },
          { value: 'medical_examination', label: 'Medical Examination' }
        ], help: 'Document/event that triggers eligibility for this leave type', section: 'advanced' },
        { name: 'leave_type_trigger_deadline_months', label: 'Trigger Deadline (Months)', type: 'number', required: false, min: 0, help: 'Months after trigger event when leave must be consumed', section: 'advanced' },
        { name: 'leave_type_mom_reimbursement', label: '💰 Government Reimbursement', type: 'checkbox', required: false, help: 'Whether government provides reimbursement for this leave type', section: 'advanced' },
        { name: 'leave_type_reimbursement_cap_per_week', label: 'Weekly Reimbursement Cap (S$)', type: 'number', required: false, min: 0, step: '0.01', help: 'Weekly reimbursement cap from government (e.g., S$2,500/week for GPML/GPPL)', section: 'advanced' },
            { name: 'leave_type_total_reimbursement_cap', label: 'Total Reimbursement Cap (S$)', type: 'number', required: false, min: 0, step: '0.01', help: 'Total reimbursement cap for entire leave scheme (e.g., S$40,000 for GPML per child)', section: 'advanced' },
        { name: 'leave_type_reimbursement_cap_period_weeks', label: 'Reimbursement Period (Weeks)', type: 'number', required: false, min: 0, help: 'Number of weeks the reimbursement cap applies to (e.g., 16 for GPML)', section: 'advanced' },
        { name: 'leave_type_cap_is_per_child', label: '👶 Cap Per Child', type: 'checkbox', required: false, help: 'Whether program cap is per child (for maternity/paternity/adoption leave)', section: 'advanced' },
        
        // Versioning Fields (New for MOM Policy Changes)
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true, help: 'Date when this MOM policy becomes effective', section: 'versioning' },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false, help: 'Date when this MOM policy expires (leave empty for ongoing policies)', section: 'versioning' },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false, help: 'Whether this leave type version is currently active', section: 'versioning' }
      ],
      publicHolidays: [
        { name: 'holiday_name', label: 'Holiday Name', type: 'text', required: true },
        { name: 'holiday_date', label: 'Date', type: 'date', required: true },
        { name: 'holiday_type', label: 'Type', type: 'select', required: true, options: [
          { value: 'national', label: 'National Holiday' },
          { value: 'religious', label: 'Religious Holiday' },
          { value: 'cultural', label: 'Cultural Holiday' },
          { value: 'observance', label: 'Observance Day' }
        ]},
        { name: 'holiday_description', label: 'Description', type: 'textarea', required: false },
        { name: 'holiday_is_gazetted', label: 'Is Gazetted', type: 'checkbox', required: false },
        { name: 'holiday_affects_payroll', label: 'Affects Payroll', type: 'checkbox', required: false }
      ],
      systemSettings: [
        { name: 'key', label: 'Setting Key', type: 'text', required: true },
        { name: 'value', label: 'Value', type: 'json', required: true },
        { name: 'description', label: 'Description', type: 'textarea', required: false }
      ],
      cpfPrYearRates: [
        { name: 'pr_year', label: 'PR Year', type: 'select', required: true, options: [
          { value: '1st', label: '1st Year' },
          { value: '2nd', label: '2nd Year' },
          { value: '3rd+', label: '3rd Year and Beyond' }
        ]},
        { name: 'age_bracket', label: 'Age Bracket', type: 'text', required: true },
        { name: 'employee_rate', label: 'Employee Rate (%)', type: 'number', required: true, step: '0.01' },
        { name: 'employer_rate', label: 'Employer Rate (%)', type: 'number', required: true, step: '0.01' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false }
      ],
      cpfRates: [
        { name: 'age_bracket', label: 'Age Bracket', type: 'select', required: true, 
          options: [
            { value: 'AGE_55_BELOW', label: '55 and Below' },
            { value: 'AGE_55_TO_60', label: 'Above 55 to 60' },
            { value: 'AGE_60_TO_65', label: 'Above 60 to 65' },
            { value: 'AGE_65_TO_70', label: 'Above 65 to 70' },
            { value: 'AGE_ABOVE_70', label: 'Above 70' }
          ],
          help: 'CPF age bracket for contribution rate calculation'
        },
        { name: 'employee_rate', label: 'Employee Rate (%)', type: 'number', 
          required: true, step: '0.01', min: 0, max: 100,
          help: 'Employee CPF contribution rate percentage (e.g., 20.0%)'
        },
        { name: 'employer_rate', label: 'Employer Rate (%)', type: 'number', 
          required: true, step: '0.01', min: 0, max: 100,
          help: 'Employer CPF contribution rate percentage (e.g., 17.0%)'
        },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true,
          help: 'Date when this CPF rate becomes effective'
        },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false,
          help: 'Date when this CPF rate expires (leave empty for ongoing rates)'
        },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false,
          help: 'Whether this CPF rate is currently active'
        }
      ],
      cpfWageCeilings: [
        { name: 'ow_monthly_ceiling', label: 'OW Monthly Ceiling (S$)', type: 'number', required: true, step: '0.01', help: 'Monthly salary ceiling for Ordinary Wages CPF contributions (e.g., $7,400 for 2025, $8,000 for 2026)' },
        { name: 'aw_annual_ceiling', label: 'AW Annual Ceiling (S$)', type: 'number', required: true, step: '0.01', help: 'Annual ceiling for Additional Wages CPF contributions ($102,000)' },
        { name: 'cpf_min_wage_threshold', label: 'CPF Min Wage Threshold (S$)', type: 'number', required: true, step: '0.01', help: 'Below this monthly wage, no employee CPF contribution is required ($50)' },
        { name: 'cpf_full_contribution_wage', label: 'CPF Full Contribution Wage (S$)', type: 'number', required: true, step: '0.01', help: 'Below this monthly wage, graduated CPF rates apply instead of full rates ($750)' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false }
      ],
      shgContributionTiers: [
        { name: 'fund_type', label: 'Fund Type', type: 'select', required: true, options: [
          { value: 'cdac', label: 'CDAC (Chinese)' },
          { value: 'sinda', label: 'SINDA (Indian)' },
          { value: 'mbmf', label: 'MBMF (Muslim)' },
          { value: 'ecf', label: 'ECF (Eurasian)' }
        ]},
        { name: 'wage_threshold_min', label: 'Min Wage Threshold (S$)', type: 'number', required: true, step: '0.01', help: 'Minimum monthly wage for this tier (inclusive)' },
        { name: 'wage_threshold_max', label: 'Max Wage Threshold (S$)', type: 'number', required: false, step: '0.01', help: 'Maximum monthly wage for this tier (exclusive). Leave empty for highest tier.' },
        { name: 'contribution_amount', label: 'Contribution Amount (S$)', type: 'number', required: true, step: '0.01', help: 'Contribution amount in SGD for this wage tier' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false }
      ],
      irasTaxBrackets: [
        { name: 'tax_year', label: 'Tax Year', type: 'number', required: true, min: 2020, max: 2100, help: 'Year of Assessment (e.g., 2026)' },
        { name: 'bracket_min', label: 'Bracket Minimum (S$)', type: 'number', required: true, step: '0.01', min: 0, help: 'Minimum income for this bracket (inclusive)' },
        { name: 'bracket_max', label: 'Bracket Maximum (S$)', type: 'number', required: false, step: '0.01', min: 0, help: 'Maximum income for this bracket (inclusive). Leave empty for highest bracket.' },
        { name: 'tax_rate', label: 'Tax Rate (%)', type: 'number', required: true, step: '0.01', min: 0, max: 100, help: 'Tax rate percentage (e.g., 7.00 for 7%)' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true, help: 'Date when this bracket becomes effective' },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false, help: 'Date when this bracket expires (leave empty for ongoing)' },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false, help: 'Whether this bracket is currently active' }
      ],
      irasTaxReliefs: [
        { name: 'relief_type', label: 'Relief Type', type: 'text', required: true, help: 'Relief type key (e.g., personal, child, parent)' },
        { name: 'relief_name', label: 'Relief Name', type: 'text', required: true, help: 'Human-readable relief name (e.g., Personal Relief)' },
        { name: 'tax_year', label: 'Tax Year', type: 'number', required: true, min: 2020, max: 2100, help: 'Year of Assessment (e.g., 2026)' },
        { name: 'base_amount', label: 'Base Amount (S$)', type: 'number', required: true, step: '0.01', min: 0, help: 'Base relief amount (e.g., 15000.00 for Personal Relief)' },
        { name: 'max_per_employee', label: 'Max Per Employee (S$)', type: 'number', required: false, step: '0.01', min: 0, help: 'Maximum amount per employee (leave empty if no cap)' },
        { name: 'max_instances', label: 'Max Instances', type: 'number', required: false, min: 1, help: 'Maximum instances (e.g., 5 for child relief, leave empty if unlimited)' },
        { name: 'min_age', label: 'Minimum Age', type: 'number', required: false, min: 0, max: 150, help: 'Minimum age for age-based reliefs' },
        { name: 'max_age', label: 'Maximum Age', type: 'number', required: false, min: 0, max: 150, help: 'Maximum age for age-based reliefs' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true, help: 'Date when this relief becomes effective' },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false, help: 'Date when this relief expires (leave empty for ongoing)' },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false, help: 'Whether this relief is currently active' }
      ],
      sdlPolicies: [
        { name: 'policy_name', label: 'Policy Name', type: 'text', required: true, help: 'Name for this SDL policy version (e.g., "Singapore SDL Policy 2026")' },
        { name: 'sdl_rate_percentage', label: 'SDL Rate (%)', type: 'number', required: true, step: '0.0001', min: 0, max: 10, help: 'SDL rate as percentage (e.g., 0.25 for 0.25%). Rounding Rule: Sum all employee SDLs, then round total down to nearest dollar.' },
        { name: 'minimum_wage_threshold', label: 'Minimum Wage Threshold (S$)', type: 'number', required: true, step: '0.01', min: 0, help: 'Wage threshold below which minimum SDL applies (e.g., $800)' },
        { name: 'wage_ceiling', label: 'Wage Ceiling (S$)', type: 'number', required: true, step: '0.01', min: 0, help: 'Maximum wages subject to SDL calculation (e.g., $4,500)' },
        { name: 'minimum_sdl_amount', label: 'Minimum SDL (S$)', type: 'number', required: true, step: '0.01', min: 0, help: 'Minimum SDL payable per month (e.g., $2)' },
        { name: 'maximum_sdl_amount', label: 'Maximum SDL (S$)', type: 'number', required: true, step: '0.01', min: 0, help: 'Maximum SDL payable per month (e.g., $11.25)' },
        { name: 'includes_bonuses', label: 'Includes Bonuses', type: 'checkbox', required: false, help: 'Whether bonuses are included in SDL wage calculation' },
        { name: 'includes_allowances', label: 'Includes Allowances', type: 'checkbox', required: false, help: 'Whether allowances are included in SDL wage calculation' },
        { name: 'includes_overtime_pay', label: 'Includes Overtime Pay', type: 'checkbox', required: false, help: 'Whether overtime pay is included in SDL wage calculation' },
        { name: 'includes_commission', label: 'Includes Commission', type: 'checkbox', required: false, help: 'Whether commission is included in SDL wage calculation' },
        { name: 'includes_public_holiday_pay', label: 'Includes Public Holiday Pay', type: 'checkbox', required: false, help: 'Whether public holiday pay is included in SDL wage calculation' },
        { name: 'includes_rest_day_pay', label: 'Includes Rest Day Pay', type: 'checkbox', required: false, help: 'Whether rest day pay is included in SDL wage calculation' },
        { name: 'requires_service_in_singapore', label: 'Requires Service in Singapore', type: 'checkbox', required: false, help: 'Whether employee must render service in Singapore for SDL liability' },
        { name: 'policy_description', label: 'Policy Description', type: 'textarea', required: false, help: 'Description of policy changes and implementation notes' },
        { name: 'mom_reference_url', label: 'MOM Reference URL', type: 'url', required: false, help: 'URL to official MOM SDL policy documentation' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true, help: 'Date when this policy becomes effective' },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false, help: 'Date when this policy expires (leave empty for ongoing/current policy)' },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false, help: 'Whether this policy is currently active' }
      ],
      workweekPolicies: [
        { name: 'hours_per_week', label: 'Hours per Week', type: 'number', required: true },
        { name: 'break_hours_required', label: 'Break Hours Required', type: 'number', required: true, step: '0.5' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false }
      ],
      // workScheduleTemplates uses the dedicated renderWorkScheduleTemplateForm() path
      // and its full field definition lives in the first fieldConfigs entry above (lines 168–217).
      // No second definition needed here.
      overtimePolicies: [
        { name: 'multiplier', label: 'Overtime Multiplier', type: 'number', required: true, step: '0.1' },
        { name: 'daily_limit_hours', label: 'Daily Limit Hours', type: 'number', required: true, step: '0.5' },
        { name: 'weekly_limit_hours', label: 'Weekly Limit Hours', type: 'number', required: true, step: '0.5' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false }
      ],
      approvalRules: [
        { name: 'entity', label: 'Entity', type: 'text', required: true },
        { name: 'action', label: 'Action', type: 'text', required: true },
        { name: 'approver_role', label: 'Approver Role', type: 'text', required: true },
        { name: 'conditions', label: 'Conditions (JSON)', type: 'json', required: false }
      ],
      notificationTemplates: [
        { name: 'name', label: 'Template Name', type: 'text', required: true },
        { name: 'channel', label: 'Channel', type: 'select', required: true, options: [
          { value: 'email', label: 'Email' },
          { value: 'sms', label: 'SMS' },
          { value: 'in-app', label: 'In-App' }
        ]},
        { name: 'subject_template', label: 'Subject Template', type: 'text', required: true },
        { name: 'body_template', label: 'Body Template', type: 'textarea', required: true }
      ],
      maternityLeavePolicies: [
        // Service Requirements
        { name: 'minimum_service_months', label: '🇸🇬 Minimum Service Months', type: 'number', required: true, min: 0, help: 'Minimum service months required for paid maternity leave', section: 'service' },
        
        // Leave Duration Settings
        { name: 'citizen_child_weeks', label: '🇸🇬 Citizen Child Weeks (GPML)', type: 'number', required: true, min: 1, help: 'GPML duration in weeks for Singapore citizen children', section: 'duration' },
        { name: 'non_citizen_child_weeks', label: '🌍 Non-Citizen Child Weeks', type: 'number', required: true, min: 1, help: 'Leave duration in weeks for non-citizen children', section: 'duration' },
        { name: 'unpaid_leave_weeks', label: '❌ Unpaid Leave Weeks', type: 'number', required: true, min: 1, help: 'Unpaid leave duration in weeks', section: 'duration' },
        
        // Notice Requirements
        { name: 'gpml_notice_weeks', label: '📅 GPML Notice Weeks', type: 'number', required: true, min: 1, help: 'Required notice period in weeks for GPML applications', section: 'notice' },
        { name: 'non_citizen_notice_weeks', label: '📅 Non-Citizen Notice Weeks', type: 'number', required: true, min: 1, help: 'Required notice period in weeks for non-citizen child leave', section: 'notice' },
        
        // GPML Payment Structure - First/Second Child
        { name: 'gpml_first_second_employer_paid_weeks', label: '💼 1st/2nd Child - Employer Paid Weeks', type: 'number', required: true, min: 0, help: 'Weeks paid by employer for 1st/2nd child GPML', section: 'payment_first_second' },
        { name: 'gpml_first_second_govt_reimbursed_weeks', label: '🏛️ 1st/2nd Child - Govt Reimbursed Weeks', type: 'number', required: true, min: 0, help: 'Weeks reimbursed by government for 1st/2nd child GPML', section: 'payment_first_second' },
        { name: 'gpml_first_second_weekly_cap', label: '💰 1st/2nd Child - Weekly Cap', type: 'number', required: true, min: 0, step: '0.01', help: 'Weekly reimbursement cap for 1st/2nd child GPML', section: 'payment_first_second' },
        { name: 'gpml_first_second_total_cap', label: '💰 1st/2nd Child - Total Cap', type: 'number', required: true, min: 0, step: '0.01', help: 'Total reimbursement cap for 1st/2nd child GPML', section: 'payment_first_second' },
        
        // GPML Payment Structure - Third+ Child
        { name: 'gpml_third_plus_employer_paid_weeks', label: '💼 3rd+ Child - Employer Paid Weeks', type: 'number', required: true, min: 0, help: 'Weeks paid by employer for 3rd+ child GPML', section: 'payment_third_plus' },
        { name: 'gpml_third_plus_govt_reimbursed_weeks', label: '🏛️ 3rd+ Child - Govt Reimbursed Weeks', type: 'number', required: true, min: 0, help: 'Weeks reimbursed by government for 3rd+ child GPML', section: 'payment_third_plus' },
        { name: 'gpml_third_plus_weekly_cap', label: '💰 3rd+ Child - Weekly Cap', type: 'number', required: true, min: 0, step: '0.01', help: 'Weekly reimbursement cap for 3rd+ child GPML', section: 'payment_third_plus' },
        { name: 'gpml_third_plus_total_cap', label: '💰 3rd+ Child - Total Cap', type: 'number', required: true, min: 0, step: '0.01', help: 'Total reimbursement cap for 3rd+ child GPML', section: 'payment_third_plus' },
        
        // Non-Citizen Child Payment
        { name: 'non_citizen_employer_paid_weeks', label: '💼 Non-Citizen - Employer Paid Weeks', type: 'number', required: true, min: 0, help: 'Weeks paid by employer for eligible non-citizen child leave', section: 'payment_non_citizen' },
        { name: 'non_citizen_unpaid_weeks', label: '❌ Non-Citizen - Unpaid Weeks', type: 'number', required: true, min: 0, help: 'Unpaid weeks for non-citizen child leave', section: 'payment_non_citizen' },
        
        // Standard versioned setting fields
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true, help: 'Date when this policy becomes effective', section: 'admin' },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false, help: 'Whether this policy is currently active', section: 'admin' }
      ],
      foreignWorkerLevies: [
        { name: 'pass_type', label: 'Pass Type', type: 'select', required: true, options: [
          { value: 'Work Permit', label: 'Work Permit' },
          { value: 'S Pass', label: 'S Pass' },
          { value: 'Employment Pass', label: 'Employment Pass' },
          { value: 'Dependent Pass', label: 'Dependent Pass' }
        ]},
        { name: 'sector', label: 'Sector', type: 'select', required: true, options: [
          { value: 'Construction', label: 'Construction' },
          { value: 'Manufacturing', label: 'Manufacturing' },
          { value: 'Marine Shipyard', label: 'Marine Shipyard' },
          { value: 'Process', label: 'Process' },
          { value: 'Services', label: 'Services' }
        ]},
        { name: 'levy_rate_monthly', label: 'Monthly Levy Rate (SGD)', type: 'number', required: true, step: '0.01' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false }
      ],
      
      // Medical Exam Types Configuration
      medicalExams: [
        { name: 'name', label: 'Medical Exam Name', type: 'text', required: true, help: 'Name of the medical examination (e.g., "Pre-Employment Physical")' },
        { name: 'exam_type', label: 'Exam Type', type: 'select', required: true, options: [
          { value: 'pre_employment', label: 'Pre-employment' },
          { value: 'periodic_health', label: 'Periodic Health Screening' },
          { value: 'fit_for_work', label: 'Fit-For-Work' },
          { value: 'hazard_specific', label: 'Hazard-Specific Tests' }
        ], help: 'Category of medical examination' },
        { name: 'frequency_months', label: 'Frequency (Months)', type: 'number', required: false, min: 0, help: 'How often this exam is required (in months). Leave empty for one-time exams.' }
      ],
      
      // Insurance Policy Types Configuration
      insurancePolicies: [
        { name: 'name', label: 'Insurance Policy Name', type: 'text', required: true },
        { name: 'policy_type', label: 'Policy Type', type: 'select', required: true, options: [
          { value: 'health', label: 'Health Insurance' },
          { value: 'life', label: 'Life Insurance' },
          { value: 'disability', label: 'Disability Insurance' },
          { value: 'accident', label: 'Accident Insurance' },
          { value: 'travel', label: 'Travel Insurance' }
        ]},
        { name: 'coverage_amount', label: 'Coverage Amount (S$)', type: 'number', required: false, step: '0.01', min: 0 },
        { name: 'description', label: 'Description', type: 'textarea', required: false }
      ],
      
      // Benefit Plans Configuration
      benefitPlans: [
        { name: 'name', label: 'Benefit Plan Name', type: 'text', required: true },
        { name: 'plan_type', label: 'Plan Type', type: 'select', required: true, options: [
          { value: 'healthcare', label: 'Healthcare' },
          { value: 'dental', label: 'Dental' },
          { value: 'vision', label: 'Vision' },
          { value: 'wellness', label: 'Wellness Program' },
          { value: 'retirement', label: 'Retirement' }
        ]},
        { name: 'provider', label: 'Provider', type: 'text', required: false },
        { name: 'coverage_amount', label: 'Coverage Amount (S$)', type: 'number', required: false, step: '0.01', min: 0 },
        { name: 'description', label: 'Description', type: 'textarea', required: false }
      ],
      
      // Trade Categories Configuration
      tradeCategories: [
        { name: 'name', label: 'Trade Category Name', type: 'text', required: true, help: 'Name of the trade (e.g., "Carpentry", "Masonry", "Plumbing")' },
        { name: 'description', label: 'Description', type: 'textarea', required: false },
        { name: 'industry_code', label: 'Industry Code', type: 'text', required: false, help: 'Official industry classification code' }
      ],
      
      // Competency Levels Configuration
      competencyLevels: [
        { name: 'name', label: 'Competency Level Name', type: 'text', required: true, help: 'Name of the competency level (e.g., "Apprentice", "Skilled Worker", "Supervisor")' },
        { name: 'level_number', label: 'Level Number', type: 'number', required: true, min: 1, help: 'Numeric level for ordering (1 = lowest, higher numbers = more advanced)' },
        { name: 'description', label: 'Description', type: 'textarea', required: false },
        { name: 'minimum_experience_months', label: 'Minimum Experience (Months)', type: 'number', required: false, min: 0, help: 'Minimum months of experience required for this level' }
      ],
      
      // Safety Certification Types Configuration
      safetyCertifications: [
        { name: 'name', label: 'Safety Certification Name', type: 'text', required: true, help: 'Name of the safety certification (e.g., "WSH Safety Course", "Working at Heights")' },
        { name: 'description', label: 'Description', type: 'textarea', required: false },
        { name: 'validity_period_months', label: 'Validity Period (Months)', type: 'number', required: false, min: 1, help: 'How long the certification is valid for (in months)' },
        { name: 'regulatory_body', label: 'Regulatory Body', type: 'text', required: false, help: 'Authority that issues this certification (e.g., "MOM", "BCA")' }
      ],
      
      // Education Institutions Configuration
      educationInstitutions: [
        { name: 'name', label: 'Institution Name', type: 'text', required: true, help: 'Official name of the education institution' },
        { name: 'institution_type', label: 'Institution Type', type: 'select', required: false, help: 'Type of institution (e.g., University, Polytechnic, ITE)' },
        { name: 'address', label: 'Address', type: 'textarea', required: false, help: 'Physical address of the institution' },
        { name: 'postal_code', label: 'Postal Code', type: 'text', required: false, help: 'Postal code of the institution' },
        { name: 'website', label: 'Website', type: 'url', required: false, help: 'Official website URL' },
        { name: 'contact_phone', label: 'Contact Phone', type: 'text', required: false, help: 'Contact phone number' },
        { name: 'contact_email', label: 'Contact Email', type: 'email', required: false, help: 'Contact email address' },
        { name: 'country', label: 'Country (Deprecated)', type: 'text', required: false, help: '[DEPRECATED] Use alpha_two_code instead. Country name (e.g., Singapore)' },
        { name: 'alpha_two_code', label: 'Country Code (ISO)', type: 'text', required: false, maxLength: 2, help: 'ISO 3166-1 alpha-2 country code (e.g., SG, MY, US)' },
        { name: 'moe_code', label: 'MOE Code', type: 'text', required: false, help: 'Ministry of Education code if applicable' },
        { name: 'uen', label: 'UEN', type: 'text', required: false, help: 'Singapore Unique Entity Number (UEN) if applicable' },
        { name: 'is_active', label: 'Active in Library', type: 'checkbox', required: false, help: 'Whether this institution is active in the library (for soft delete)' },
        { name: 'notes', label: 'Internal Notes', type: 'textarea', required: false, help: 'HR admin notes (internal use only, not visible to employees)' }
      ],
      
      // Institution Types Configuration
      institutionTypes: [
        { name: 'name', label: 'Institution Type Name', type: 'text', required: true, help: 'Name of the institution type (e.g., University, Polytechnic, ITE)' },
        { name: 'description', label: 'Description', type: 'textarea', required: false, help: 'Description of this institution type' }
      ],

      // ── Previously missing form definitions ──────────────────────────────────

      salaryStructures: [
        { name: 'component_name', label: 'Component Name', type: 'text', required: true, help: 'Name of the salary component (e.g., Basic Salary, Housing Allowance)' },
        { name: 'component_type', label: 'Component Type', type: 'select', required: true, options: [
          { value: 'basic', label: 'Basic Salary' },
          { value: 'allowance', label: 'Allowance' },
          { value: 'deduction', label: 'Deduction' },
          { value: 'bonus', label: 'Bonus' },
          { value: 'overtime', label: 'Overtime' },
          { value: 'commission', label: 'Commission' },
        ], help: 'Category of this salary component' },
      ],

      allowanceTypes: [
        { name: 'allowance_type_name', label: 'Allowance Name', type: 'text', required: true, help: 'Name of the allowance (e.g., Transport Allowance, Meal Allowance)' },
        { name: 'allowance_type_description', label: 'Description', type: 'textarea', required: false },
        { name: 'allowance_type_requires_documentation', label: 'Requires Documentation', type: 'checkbox', required: false, help: 'Employee must submit supporting documents to claim this allowance' },
        { name: 'allowance_type_is_taxable', label: 'Taxable', type: 'checkbox', required: false, help: 'Whether this allowance is subject to income tax' },
        { name: 'allowance_type_is_cpf_applicable', label: 'CPF Applicable', type: 'checkbox', required: false, help: 'Whether CPF contributions apply to this allowance' },
      ],

      noticePolicies: [
        { name: 'service_length_min', label: 'Min Service Length (months)', type: 'number', required: true, min: 0, help: 'Minimum months of service for this notice tier to apply' },
        { name: 'service_length_max', label: 'Max Service Length (months)', type: 'number', required: false, min: 0, help: 'Maximum months of service (leave empty for no upper limit)' },
        { name: 'notice_days', label: 'Notice Days Required', type: 'number', required: true, min: 1, help: 'Number of calendar days of notice required for this service tier' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false },
      ],

      retirementPolicies: [
        { name: 'retirement_age', label: 'Retirement Age', type: 'number', required: true, min: 55, max: 75, help: 'Statutory retirement age (MOM default: 63)' },
        { name: 'reemployment_age', label: 'Re-employment Age', type: 'number', required: true, min: 60, max: 80, help: 'Maximum re-employment age (MOM default: 68)' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false },
      ],

      wicaCompensations: [
        { name: 'injury_type', label: 'Injury Type', type: 'text', required: true, help: 'Type of workplace injury or illness (e.g., Temporary Incapacity, Permanent Incapacity)' },
        { name: 'compensation_cap', label: 'Compensation Cap (S$)', type: 'number', required: true, step: '0.01', min: 0, help: 'Maximum WICA compensation amount in SGD' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false },
      ],

      workPassQuotas: [
        { name: 'pass_type', label: 'Pass Type', type: 'select', required: true, options: [
          { value: 'work_permit', label: 'Work Permit' },
          { value: 's_pass', label: 'S Pass' },
          { value: 'employment_pass', label: 'Employment Pass' },
        ], help: 'Type of work pass this quota applies to' },
        { name: 'sector', label: 'Sector', type: 'select', required: true, options: [
          { value: 'Construction', label: 'Construction' },
          { value: 'Manufacturing', label: 'Manufacturing' },
          { value: 'Marine Shipyard', label: 'Marine Shipyard' },
          { value: 'Process', label: 'Process' },
          { value: 'Services', label: 'Services' },
        ]},
        { name: 'monthly_quota_limit', label: 'Monthly Quota Limit', type: 'number', required: true, min: 0, help: 'Maximum number of passes per month for this sector' },
        { name: 'dependency_ratio_ceiling', label: 'Dependency Ratio Ceiling (%)', type: 'number', required: false, step: '0.01', min: 0, max: 100, help: 'Maximum percentage of foreign workers allowed relative to local workforce' },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true },
        { name: 'effective_to', label: 'Effective To', type: 'date', required: false },
        { name: 'is_active', label: 'Is Active', type: 'checkbox', required: false },
      ],

      integrationKeys: [
        { name: 'name', label: 'Key Name', type: 'text', required: true, help: 'Human-readable name for this integration key (e.g., WhatsApp Business API)' },
        { name: 'key_type', label: 'Key Type', type: 'text', required: true, help: 'Type identifier for this key (e.g., whatsapp_api, email_smtp, sms_gateway)' },
        { name: 'key_value', label: 'Secret Value', type: 'textarea', required: false, help: 'The secret key or token value. This field is write-only — the stored value is never displayed after saving for security.' },
        { name: 'expires_at', label: 'Expires At', type: 'date', required: false, help: 'Expiry date of this key (leave empty for non-expiring keys)' },
      ],

      auditConfigs: [
        { name: 'model_name', label: 'Model / Entity Name', type: 'text', required: true, help: 'Django model name to audit (e.g., StaffUser, LeaveApplication)' },
        { name: 'log_level', label: 'Log Level', type: 'select', required: true, options: [
          { value: 'DEBUG', label: 'DEBUG — All events' },
          { value: 'INFO', label: 'INFO — Standard operations' },
          { value: 'WARNING', label: 'WARNING — Unusual events' },
          { value: 'ERROR', label: 'ERROR — Failures only' },
        ], help: 'Minimum severity level for events to be logged' },
        { name: 'retention_days', label: 'Retention Period (days)', type: 'number', required: true, min: 1, help: 'How many days audit logs are retained before automatic deletion. MOM requires minimum 7 years (2555 days).' },
      ],

      competencyFrameworks: [
        { name: 'name', label: 'Framework Name', type: 'text', required: true, help: 'Name of the competency framework (e.g., BCA Built Environment Framework)' },
        { name: 'description', label: 'Description', type: 'textarea', required: false },
        { name: 'skills_matrix', label: 'Skills Matrix (JSON)', type: 'json', required: false, help: 'Optional structured JSON defining the skills matrix for this framework' },
      ],

      cpdRequirements: [
        { name: 'trade_category', label: 'Trade Category', type: 'select', required: true,
          options: (configData?.tradeCategories || []).map(t => ({ value: t.id, label: t.name })),
          help: 'Trade category this CPD requirement applies to' },
        { name: 'competency_level', label: 'Competency Level', type: 'select', required: false,
          options: (configData?.competencyLevels || []).map(l => ({ value: l.id, label: l.name })),
          help: 'Competency level this requirement applies to (leave empty for all levels)' },
        { name: 'annual_hours_required', label: 'Annual CPD Hours Required', type: 'number', required: true, min: 0, help: 'Number of Continuing Professional Development hours required per year' },
        { name: 'grace_period_months', label: 'Grace Period (months)', type: 'number', required: false, min: 0, help: 'Additional months allowed beyond the annual period to fulfil CPD hours' },
      ],

      qualificationLevels: [
        { name: 'name', label: 'Qualification Level Name', type: 'text', required: true, help: 'Name of the qualification level (e.g., "Nitec", "Higher Nitec", "Diploma")' },
        { name: 'level_number', label: 'Level Number', type: 'number', required: true, min: 1, help: 'Numeric rank for ordering (lower = less advanced)' },
        { name: 'description', label: 'Description', type: 'textarea', required: false },
        { name: 'country', label: 'Country', type: 'text', required: false, help: 'Country whose qualification framework this level belongs to (e.g., Singapore, Malaysia)' },
      ],

      fieldsOfStudy: [
        { name: 'name', label: 'Field of Study Name', type: 'text', required: true, help: 'Name of the academic field (e.g., Computer Science, Civil Engineering)' },
        { name: 'category', label: 'Category', type: 'select', required: false, options: [
          { value: 'engineering', label: 'Engineering & Technology' },
          { value: 'business', label: 'Business & Management' },
          { value: 'science', label: 'Science & Mathematics' },
          { value: 'arts', label: 'Arts & Humanities' },
          { value: 'healthcare', label: 'Healthcare & Medicine' },
          { value: 'education', label: 'Education' },
          { value: 'law', label: 'Law & Legal Studies' },
          { value: 'trades', label: 'Technical Trades' },
          { value: 'other', label: 'Other' },
        ], help: 'Broad academic category' },
        { name: 'description', label: 'Description', type: 'textarea', required: false },
      ],

      certificationAuthorities: [
        { name: 'name', label: 'Authority Name', type: 'text', required: true, help: 'Full official name (e.g., Building and Construction Authority)' },
        { name: 'short_name', label: 'Short Name / Acronym', type: 'text', required: false, help: 'Common abbreviation (e.g., BCA, MOM, SCDF)' },
        { name: 'description', label: 'Description', type: 'textarea', required: false },
        { name: 'country', label: 'Country', type: 'text', required: false, help: 'Country of authority (e.g., Singapore)' },
        { name: 'website', label: 'Website URL', type: 'url', required: false },
        { name: 'is_government', label: 'Government Authority', type: 'checkbox', required: false, help: 'Whether this is an official government body' },
      ],

      certificationCategories: [
        { name: 'name', label: 'Category Name', type: 'text', required: true, help: 'Name of the certification category (e.g., Safety, Trade, Professional)' },
        { name: 'description', label: 'Description', type: 'textarea', required: false },
        { name: 'industry_focus', label: 'Industry Focus', type: 'select', required: false, options: [
          { value: 'construction', label: 'Construction' },
          { value: 'manufacturing', label: 'Manufacturing' },
          { value: 'marine', label: 'Marine & Offshore' },
          { value: 'oil_gas', label: 'Oil & Gas / Process' },
          { value: 'general', label: 'General / Cross-industry' },
        ], help: 'Primary industry this certification category serves' },
      ],

      certificationTemplates: [
        { name: 'name', label: 'Certificate Name', type: 'text', required: true, help: 'Full name of the certificate (e.g., WSH Safety Course for Managers & Supervisors)' },
        { name: 'short_name', label: 'Short Name', type: 'text', required: false, help: 'Common abbreviation used internally' },
        { name: 'issuing_authority', label: 'Issuing Authority', type: 'select', required: true,
          options: (configData?.certificationAuthorities || []).map(a => ({ value: a.id, label: a.name })),
          help: 'Authority that issues this certificate' },
        { name: 'category', label: 'Category', type: 'select', required: false,
          options: (configData?.certificationCategories || []).map(c => ({ value: c.id, label: c.name })),
          help: 'Certification category' },
        { name: 'validity_period_months', label: 'Validity Period (months)', type: 'number', required: false, min: 1, help: 'How long this certificate is valid after issuance (leave empty if lifetime)' },
        { name: 'is_renewable', label: 'Renewable', type: 'checkbox', required: false, help: 'Whether this certificate can be renewed after expiry' },
        { name: 'description', label: 'Description', type: 'textarea', required: false },
      ],

      skillFrameworks: [
        { name: 'trade_category', label: 'Trade Category', type: 'select', required: true,
          options: (configData?.tradeCategories || []).map(t => ({ value: t.id, label: t.name })),
          help: 'Trade this skill framework entry applies to' },
        { name: 'competency_level', label: 'Competency Level', type: 'select', required: true,
          options: (configData?.competencyLevels || []).map(l => ({ value: l.id, label: l.name })),
          help: 'Competency level within the trade' },
        { name: 'recertification_interval_months', label: 'Recertification Interval (months)', type: 'number', required: false, min: 1, help: 'How often recertification is required for this trade/level combination' },
      ],
    };
    
    return fieldConfigs[category] || [];
  };

  // Load staff users for department HOD selection and leave types for leave policies
  useEffect(() => {
    const loadStaffUsers = async () => {
      if (category === 'departments' && isOpen) {
        try {
          const response = await api.get('/staff/');
          setStaffUsers(response.data.results || response.data || []);
        } catch (error) {
          console.error('Error loading staff users:', error);
          setStaffUsers([]);
        }
      }
    };
    
    const loadLeaveTypes = async () => {
      // No longer needed since leaveEntitlements was removed
    };
    
    loadStaffUsers();
    loadLeaveTypes();
  }, [category, isOpen]);

  // Get fields after loading dependencies
  const fields = getFormFields(category);



  useEffect(() => {
    if (item) {
      setFormData(item);
    } else {
      // Initialize form with default values
      const initialData = {};
      fields.forEach(field => {
        if (field.type === 'checkbox') {
          initialData[field.name] = false;
        } else if (field.type === 'json') {
          initialData[field.name] = {};
        } else if (field.name === 'calculation_method') {
          initialData[field.name] = 'accrual'; // Default to accrual method
        } else if (field.name === 'pro_rating_method') {
          initialData[field.name] = 'none'; // Default to no pro-rating
        } else if (field.name === 'annual_accrual_days') {
          initialData[field.name] = 0; // Default to 0 (user can change if needed)
        } else if (field.name === 'service_months_required') {
          initialData[field.name] = 0; // Default to no minimum service
        } else if (field.name === 'is_active') {
          initialData[field.name] = true; // Default to active
        } else if (category === 'leaveEntitlements' && field.name === 'effective_from') {
          // Default to today's date for leave policies
          initialData[field.name] = getTodayInSingaporeTimezone();
        } else {
          initialData[field.name] = '';
        }
      });
      setFormData(initialData);
    }
  }, [item, category, staffUsers]);

  const validateForm = () => {
    const errors = {};
    
    fields.forEach(field => {
      if (field.required && (formData[field.name] === null || formData[field.name] === undefined || formData[field.name] === '')) {
        errors[field.name] = `${field.label} is required`;
      }
      
      if (field.type === 'number' && formData[field.name] && isNaN(formData[field.name])) {
        errors[field.name] = `${field.label} must be a valid number`;
      }
      
      if (field.type === 'json' && formData[field.name] && typeof formData[field.name] === 'string') {
        try {
          JSON.parse(formData[field.name]);
        } catch (e) {
          errors[field.name] = `${field.label} must be valid JSON`;
        }
      }
    });

    // Additional validation for leave policies
            if (category === 'leaveEntitlements') {
      if (formData.effective_to && formData.effective_from && 
          new Date(formData.effective_to) <= new Date(formData.effective_from)) {
        errors.effective_to = 'Effective To date must be after Effective From date';
      }
      
      if (formData.carry_forward_allowed && formData.carry_forward_max_days && 
          parseInt(formData.carry_forward_max_days) > parseInt(formData.entitlement_days)) {
        errors.carry_forward_max_days = 'Carry forward days cannot exceed entitlement days';
      }
      
      if (formData.maximum_entitlement_days && formData.entitlement_days &&
          parseInt(formData.maximum_entitlement_days) < parseInt(formData.entitlement_days)) {
        errors.maximum_entitlement_days = 'Maximum entitlement cannot be less than base entitlement';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Convert camelCase category name to kebab-case API endpoint
  const getCategoryEndpoint = (category) => {
    const endpointMap = {
      departments: 'departments',
      locations: 'locations',
      classifications: 'classifications',
      designations: 'designations',
      employmentTypes: 'employment-types',
      payGrades: 'pay-grades',
      salaryStructures: 'salary-structures',
      leaveTypes: 'leave-types',
      
      holidayCalendars: 'holiday-calendars',
      publicHolidays: 'public-holidays',
      cpfRates: 'cpf-contribution-rates',
          workweekPolicies: 'workweek-policies',
    workScheduleTemplates: 'work-schedule-templates',
    overtimePolicies: 'overtime-policies',
      noticePolicies: 'notice-period-policies',
      retirementPolicies: 'retirement-policies',
      foreignWorkerLevies: 'foreign-worker-levies',
      wicaCompensations: 'wica-compensations',
      systemSettings: 'system-settings',
      integrationKeys: 'integration-keys',
      customFields: 'custom-fields',
      approvalRules: 'approval-rules',
      notificationTemplates: 'notification-templates',
      auditConfigs: 'audit-logging-configs',
      safetyCertifications: 'safety-certification-types',
      tradeCategories: 'trade-categories',
      competencyLevels: 'competency-levels',
      skillFrameworks: 'skill-frameworks',
      benefitPlans: 'benefit-plans',
      insurancePolicies: 'insurance-policy-types',
      medicalExams: 'medical-exam-types',
      competencyFrameworks: 'competency-frameworks',
      cpdRequirements: 'cpd-requirements',
      workPassQuotas: 'work-pass-quotas'
    };
    return endpointMap[category] || category;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const endpointName = getCategoryEndpoint(category);
      // Handle different API prefixes - leave types and allowance types are in staff_core
      // All config types now use configuration prefix (allowanceTypes moved from staff_core)
      const apiPrefix = 'configuration';
      const endpoint = `/${apiPrefix}/${endpointName}/`;
      const payload = { ...formData };
      
      // Handle JSON fields
      fields.forEach(field => {
        if (field.type === 'json' && typeof payload[field.name] === 'string') {
          try {
            payload[field.name] = JSON.parse(payload[field.name]);
          } catch (e) {
            payload[field.name] = {};
          }
        }
        
        // Convert number fields to proper numbers (allow 0 as valid)
        if (field.type === 'number') {
          if (payload[field.name] === '' || payload[field.name] === null || payload[field.name] === undefined) {
            // For non-required number fields, set to 0 instead of empty string
            if (!field.required) {
              payload[field.name] = 0;
            } else {
              // Keep empty for validation to catch required fields
              delete payload[field.name];
            }
          } else {
            payload[field.name] = Number(payload[field.name]);
          }
        }
        
        // Ensure boolean fields are proper booleans
        if (field.type === 'checkbox') {
          payload[field.name] = Boolean(payload[field.name]);
        }
        
        // Handle date fields - convert empty strings to null for optional dates
        if (field.type === 'date') {
          if (payload[field.name] === '' || payload[field.name] === null || payload[field.name] === undefined) {
            if (!field.required) {
              payload[field.name] = null;
            } else {
              // Keep empty for validation to catch required fields
              delete payload[field.name];
            }
          }
          // Django expects YYYY-MM-DD format, which HTML date inputs provide
        }
      });

      // Debug logging for leave policies
      if (category === 'leaveEntitlements') {
        console.log('🚀 Submitting Leave Policy Data:', payload);
        console.log('📋 Required fields check:', {
          name: payload.name,
          leave_type: payload.leave_type,
          employment_classification: payload.employment_classification,
          entitlement_days: payload.entitlement_days,
          statutory_minimum_days: payload.statutory_minimum_days,
          effective_from: payload.effective_from
        });
      }
      
      let response;
      if (item && item.id) {
        response = await api.put(`${endpoint}${item.id}/`, payload);
      } else {
        response = await api.post(endpoint, payload);
      }
      
      onSave(response.data);
      onClose();
    } catch (err) {
      console.error('Error saving configuration:', err);
      
      // Handle validation errors from Django REST framework
      if (err.response?.data) {
        const errorData = err.response.data;
        console.error('Validation errors:', errorData);
        
                 // If it's field validation errors, show them specifically
         if (typeof errorData === 'object' && !errorData.message) {
           const fieldErrors = Object.entries(errorData)
             .map(([field, errors]) => {
               const errorList = Array.isArray(errors) ? errors : [errors];
               return `${field}: ${errorList.join(', ')}`;
             })
             .join('\n');
           setError(`Validation errors:\n${fieldErrors}`);
         } else if (errorData.non_field_errors) {
           // Handle unique constraint violations and other non-field errors
           setError(`Error: ${Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors.join(', ') : errorData.non_field_errors}`);
         } else {
           setError(errorData.message || errorData.detail || JSON.stringify(errorData));
         }
      } else {
        setError('Failed to save configuration. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when field is modified
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const renderField = (field) => {
    const { name, label, type, required, options, step } = field;
    const hasError = validationErrors[name];
    
    const commonProps = {
      id: name,
      name,
      required,
      className: `mt-1 block w-full rounded-xl border-gray-300 shadow-sm focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500 sm:text-sm transition-colors ${
        hasError ? 'border-red-300' : ''
      }`
    };

    switch (type) {
      case 'text':
        return (
          <input
            {...commonProps}
            type="text"
            value={formData[name] || ''}
            onChange={(e) => handleChange(name, e.target.value)}
          />
        );
      
      case 'email':
        return (
          <input
            {...commonProps}
            type="email"
            value={formData[name] || ''}
            onChange={(e) => handleChange(name, e.target.value)}
          />
        );
      
      case 'number':
        return (
          <input
            {...commonProps}
            type="number"
            step={step}
            value={formData[name] || ''}
            onChange={(e) => handleChange(name, e.target.value)}
          />
        );
      
      case 'date':
        return (
          <DatePickerField
            selected={parseLocalDate(formData[name])}
            onChange={(date) => handleChange(name, date ? toLocalISODate(date) : '')}
            id={commonProps.id}
            name={commonProps.name}
            required={commonProps.required}
            className={`${commonProps.className} pl-12`}
            placeholderText="Select date"
            isClearable={!required}
          />
        );
      
      case 'time':
        // Convert HH:MM:SS to HH:MM for HTML time input
        const timeValue = formData[name] ? formData[name].substring(0, 5) : '';
        return (
          <input
            {...commonProps}
            type="time"
            value={timeValue}
            onChange={(e) => {
              // Convert HH:MM to HH:MM:SS for backend
              const fullTimeValue = e.target.value ? `${e.target.value}:00` : '';
              handleChange(name, fullTimeValue);
            }}
          />
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center mt-1">
            <input
              {...commonProps}
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-red-600 shadow-sm focus:border-red-500 focus-visible:ring-red-500 transition-colors"
              checked={formData[name] || false}
              onChange={(e) => handleChange(name, e.target.checked)}
            />
            <label htmlFor={name} className="ml-2 text-sm text-gray-600">
              {label}
            </label>
          </div>
        );
      
      case 'select':
        return (
          <select
            {...commonProps}
            value={formData[name] || ''}
            onChange={(e) => handleChange(name, e.target.value)}
          >
            <option value="">Select {label}</option>
            {options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={3}
            value={formData[name] || ''}
            onChange={(e) => handleChange(name, e.target.value)}
          />
        );
      
      case 'json':
        return (
          <textarea
            {...commonProps}
            rows={4}
            placeholder="Enter valid JSON"
            value={typeof formData[name] === 'object' ? JSON.stringify(formData[name], null, 2) : formData[name] || ''}
            onChange={(e) => handleChange(name, e.target.value)}
          />
        );
      
      case 'calculation_method_select':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Accrual Method Card */}
              <button
                type="button"
                onClick={() => handleChange(name, 'accrual')}
                className={`text-left p-4 rounded-xl border-2 transition-all hover:shadow-sm ${
                  formData[name] === 'accrual' 
                    ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' 
                    : 'border-gray-200 hover:border-blue-200 bg-white'
                }`}
              >
                <div className="flex items-center mb-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 ${
                    formData[name] === 'accrual' ? 'bg-blue-600' : 'bg-gray-300'
                  }`}>
                    {formData[name] === 'accrual' ? (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                    )}
                  </div>
                  <h4 className={`font-medium text-sm ${formData[name] === 'accrual' ? 'text-blue-900' : 'text-gray-700'}`}>
                    Accrual Method
                  </h4>
                  {formData[name] === 'accrual' && (
                    <span className="ml-auto text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">SELECTED</span>
                  )}
                </div>
                <p className={`text-xs ${formData[name] === 'accrual' ? 'text-blue-700' : 'text-gray-500'}`}>
                  Leave earned gradually over time (e.g., 1.75 days/month)
                </p>
                <div className={`text-xs mt-2 ${formData[name] === 'accrual' ? 'text-blue-600' : 'text-gray-400'}`}>
                  ✓ MOM compliant • ✓ Pro-rated • ✓ Prevents over-allocation
                </div>
              </button>
              
              {/* Front Loaded Method Card */}
              <button
                type="button"
                onClick={() => handleChange(name, 'front_loaded')}
                className={`text-left p-4 rounded-xl border-2 transition-all hover:shadow-sm ${
                  formData[name] === 'front_loaded' 
                    ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' 
                    : 'border-gray-200 hover:border-blue-200 bg-white'
                }`}
              >
                <div className="flex items-center mb-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 ${
                    formData[name] === 'front_loaded' ? 'bg-blue-600' : 'bg-gray-300'
                  }`}>
                    {formData[name] === 'front_loaded' ? (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                    )}
                  </div>
                  <h4 className={`font-medium text-sm ${formData[name] === 'front_loaded' ? 'text-blue-900' : 'text-gray-700'}`}>
                    Front Loaded
                  </h4>
                  {formData[name] === 'front_loaded' && (
                    <span className="ml-auto text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">SELECTED</span>
                  )}
                </div>
                <p className={`text-xs ${formData[name] === 'front_loaded' ? 'text-blue-700' : 'text-gray-500'}`}>
                  Full annual entitlement given at start of year
                </p>
                <div className={`text-xs mt-2 ${formData[name] === 'front_loaded' ? 'text-blue-600' : 'text-gray-400'}`}>
                  ✓ Simple admin • ✓ Immediate availability • ✓ Easy payroll
                </div>
              </button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  // Special layout for Work Schedule Templates
  const renderWorkScheduleTemplateForm = () => (
    <Portal>
      <div className="modal-overlay">
      <div className="modal-container modal-container-xl">
        <div className="modal-header">
          <h3 className="modal-title">
            {item ? `Edit ${title}` : `Add New ${title}`}
          </h3>
          <button onClick={onClose} className="modal-close-admin">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="modal-content">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          <form id="schedule-form" onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Basic Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['name', 'schedule_type', 'description'].map(fieldName => {
                const field = fields.find(f => f.name === fieldName);
                return field ? (
                  <div key={field.name} className={field.name === 'description' ? 'md:col-span-2' : ''}>
                    <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-2">
                      <div className="flex items-center space-x-2">
                        <span>
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </span>
                        <Tooltip content={getFieldHelp(category, field.name)}>
                          <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </Tooltip>
                      </div>
                    </label>
                    {renderField(field)}
                    {validationErrors[field.name] && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors[field.name]}</p>
                    )}
                  </div>
                ) : null;
              })}
            </div>
          </div>

          {/* Working Hours */}
          <div className="bg-blue-50 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Working Hours
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['start_time', 'end_time', 'lunch_break_start', 'lunch_break_end'].map(fieldName => {
                const field = fields.find(f => f.name === fieldName);
                return field ? (
                  <div key={field.name}>
                    <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-2">
                      <div className="flex items-center space-x-2">
                        <span>
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </span>
                        <Tooltip content={getFieldHelp(category, field.name)}>
                          <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </Tooltip>
                      </div>
                    </label>
                    {renderField(field)}
                    {validationErrors[field.name] && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors[field.name]}</p>
                    )}
                  </div>
                ) : null;
              })}
            </div>
          </div>

          {/* Working Days */}
          <div className="bg-green-50 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Working Days
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {['monday_enabled', 'tuesday_enabled', 'wednesday_enabled', 'thursday_enabled', 'friday_enabled', 'saturday_enabled', 'sunday_enabled'].map(fieldName => {
                const field = fields.find(f => f.name === fieldName);
                return field ? (
                  <div key={field.name}>
                    {renderField(field)}
                    {validationErrors[field.name] && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors[field.name]}</p>
                    )}
                  </div>
                ) : null;
              })}
            </div>
          </div>

          {/* Saturday Options */}
          <div className="bg-yellow-50 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Saturday Options
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['saturday_half_day', 'saturday_end_time'].map(fieldName => {
                const field = fields.find(f => f.name === fieldName);
                return field ? (
                  <div key={field.name}>
                    <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-2">
                      <div className="flex items-center space-x-2">
                        <span>
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </span>
                        <Tooltip content={getFieldHelp(category, field.name)}>
                          <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </Tooltip>
                      </div>
                    </label>
                    {renderField(field)}
                    {validationErrors[field.name] && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors[field.name]}</p>
                    )}
                  </div>
                ) : null;
              })}
            </div>
          </div>

          {/* Compliance Guardrails */}
          <div className="bg-red-50 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Compliance Guardrails
            </h4>
            <div className="space-y-4">
              {/* Daily Work Hours Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div>
                  <label htmlFor="enforce_daily_limits" className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <input
                      type="checkbox"
                      id="enforce_daily_limits"
                      name="enforce_daily_limits"
                      defaultChecked={true}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus-visible:ring-blue-500 focus-visible:ring-2"
                    />
                    <span>Enforce Daily Limits</span>
                    <Tooltip content={getFieldHelp(category, 'enforce_daily_limits')}>
                      <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </Tooltip>
                  </label>
                </div>
                <div>
                  <label htmlFor="max_daily_work_hours" className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center space-x-2">
                      <span>Max Daily Work Hours</span>
                      <Tooltip content={getFieldHelp(category, 'max_daily_work_hours')}>
                        <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </Tooltip>
                    </div>
                  </label>
                  <input
                    type="number"
                    id="max_daily_work_hours"
                    name="max_daily_work_hours"
                    step="0.01"
                    min="0"
                    defaultValue="12.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Monthly Overtime Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div>
                  <label htmlFor="enforce_monthly_limits" className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <input
                      type="checkbox"
                      id="enforce_monthly_limits"
                      name="enforce_monthly_limits"
                      defaultChecked={true}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus-visible:ring-blue-500 focus-visible:ring-2"
                    />
                    <span>Enforce Monthly Limits</span>
                    <Tooltip content={getFieldHelp(category, 'enforce_monthly_limits')}>
                      <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </Tooltip>
                  </label>
                </div>
                <div>
                  <label htmlFor="max_monthly_overtime_hours" className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center space-x-2">
                      <span>Max Monthly Overtime Hours</span>
                      <Tooltip content={getFieldHelp(category, 'max_monthly_overtime_hours')}>
                        <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </Tooltip>
                    </div>
                  </label>
                  <input
                    type="number"
                    id="max_monthly_overtime_hours"
                    name="max_monthly_overtime_hours"
                    step="0.01"
                    min="0"
                    defaultValue="72.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Day Reset Time Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="day_reset_time" className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center space-x-2">
                      <span>Day Reset Time</span>
                      <Tooltip content={getFieldHelp(category, 'day_reset_time')}>
                        <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </Tooltip>
                    </div>
                  </label>
                  <input
                    type="time"
                    id="day_reset_time"
                    name="day_reset_time"
                    defaultValue="00:00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
          
          </form>
        </div>
        
        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-admin-outline"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-admin-primary"
            form="schedule-form"
          >
            {loading ? 'Saving...' : (item ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );

  // Get category-specific styling and organization
  const getCategoryConfig = (category) => {
    const configs = {
      // Organization & Structure
      departments: { 
        icon: '🏢', 
        color: 'blue', 
        sections: [
          { name: 'Basic Info', fields: ['department_name', 'dept_code', 'description'] },
          { name: 'Management', fields: ['manager', 'head_of_department'] },
          { name: 'Settings', fields: ['is_active'] }
        ]
      },
      locations: { 
        icon: '📍', 
        color: 'green', 
        sections: [
          { name: 'Location Details', fields: ['location_name', 'address', 'postal_code'] },
          { name: 'Contact Info', fields: ['phone', 'email'] },
          { name: 'Settings', fields: ['is_active'] }
        ]
      },
      
      // Employee Classifications
      classifications: { 
        icon: '👥', 
        color: 'purple', 
        sections: [
          { name: 'Classification Info', fields: ['classification_name', 'description'] },
          { name: 'Settings', fields: ['is_active'] }
        ]
      },
      designations: { 
        icon: '💼', 
        color: 'indigo', 
        sections: [
          { name: 'Designation Details', fields: ['designation_name', 'description', 'level'] },
          { name: 'Settings', fields: ['is_active'] }
        ]
      },
      
      // Leave & Time Off
      leaveTypes: { 
        icon: '🏖️', 
        color: 'cyan', 
        sections: [
          { name: 'Basic Information', fields: ['leave_type_name', 'employment_classification', 'leave_type_description', 'leave_type_statutory_days'] },
          { name: '📊 Leave Entitlements', fields: ['entitlement_days', 'maximum_entitlement_days', 'annual_accrual_days', 'statutory_minimum_days', 'service_months_required'] },
          { name: '⚙️ Policy Settings', fields: ['calculation_method', 'carry_forward_allowed', 'carry_forward_max_days', 'pro_rating_method'] },
          { name: 'Leave Rules', fields: ['leave_type_is_paid', 'leave_type_requires_medical_cert', 'leave_type_advance_notice_days'] },
          { name: 'MOM Compliance', fields: ['leave_type_is_mom_statutory', 'leave_type_citizen_only'] },
          { name: 'Advanced Settings', fields: ['leave_type_max_consecutive_days', 'leave_type_trigger_event', 'leave_type_trigger_deadline_months', 'leave_type_mom_reimbursement', 'leave_type_reimbursement_cap_per_week', 'leave_type_total_reimbursement_cap', 'leave_type_reimbursement_cap_period_weeks', 'leave_type_cap_is_per_child'], collapsible: true },
          { name: '📅 Policy Versioning', fields: ['effective_from', 'effective_to', 'is_active'], collapsible: false }
        ]
      },
      maternityLeavePolicies: { 
        icon: '👶', 
        color: 'pink', 
        sections: [
          { name: '🇸🇬 Service Requirements', fields: ['minimum_service_months'] },
          { name: '📅 Leave Duration', fields: ['citizen_child_weeks', 'non_citizen_child_weeks', 'unpaid_leave_weeks'] },
          { name: '⏰ Notice Requirements', fields: ['gpml_notice_weeks', 'non_citizen_notice_weeks'] },
          { name: '💰 1st/2nd Child Payment', fields: ['gpml_first_second_employer_paid_weeks', 'gpml_first_second_govt_reimbursed_weeks', 'gpml_first_second_weekly_cap', 'gpml_first_second_total_cap'] },
          { name: '🎯 3rd+ Child Payment', fields: ['gpml_third_plus_employer_paid_weeks', 'gpml_third_plus_govt_reimbursed_weeks', 'gpml_third_plus_weekly_cap', 'gpml_third_plus_total_cap'] },
          { name: '🌍 Non-Citizen Payment', fields: ['non_citizen_employer_paid_weeks', 'non_citizen_unpaid_weeks'] },
          { name: '⚙️ Settings', fields: ['effective_from', 'is_active'] }
        ]
      },

      
      // Payroll & Benefits
      salaryStructures: { 
        icon: '💰', 
        color: 'emerald', 
        sections: [
          { name: 'Structure Info', fields: ['component_name', 'component_type', 'description'] },
          { name: 'Calculation', fields: ['amount', 'percentage', 'is_taxable'] },
          { name: 'Settings', fields: ['is_active'] }
        ]
      },
      cpfRates: { 
        icon: '🏦', 
        color: 'blue', 
        sections: [
          { name: 'Age & Rates', fields: ['age_bracket', 'employee_rate', 'employer_rate'] },
          { name: 'Validity', fields: ['effective_from', 'effective_to'] }
        ]
      },
      cpfPrYearRates: { 
        icon: '📊', 
        color: 'indigo', 
        sections: [
          { name: 'PR Year & Rates', fields: ['pr_year', 'age_bracket', 'employee_rate', 'employer_rate'] },
          { name: 'Validity', fields: ['effective_from', 'effective_to', 'is_active'] }
        ]
      },
      cpfWageCeilings: { 
        icon: '💰', 
        color: 'green', 
        sections: [
          { name: 'Wage Ceilings', fields: ['ow_monthly_ceiling', 'aw_annual_ceiling'] },
          { name: 'Thresholds', fields: ['cpf_min_wage_threshold', 'cpf_full_contribution_wage'] },
          { name: 'Validity', fields: ['effective_from', 'effective_to', 'is_active'] }
        ]
      },
      shgContributionTiers: { 
        icon: '🤝', 
        color: 'purple', 
        sections: [
          { name: 'Fund & Tier', fields: ['fund_type', 'wage_threshold_min', 'wage_threshold_max', 'contribution_amount'] },
          { name: 'Validity', fields: ['effective_from', 'effective_to', 'is_active'] }
        ]
      },
      
      // Work Policies
      workweekPolicies: { 
        icon: '⏰', 
        color: 'orange', 
        sections: [
          { name: 'Work Hours', fields: ['hours_per_week', 'break_hours_required'] },
          { name: 'Validity', fields: ['effective_from', 'effective_to'] }
        ]
      },
      overtimePolicies: { 
        icon: '🕐', 
        color: 'red', 
        sections: [
          { name: 'Overtime Rules', fields: ['multiplier', 'daily_limit_hours'] },
          { name: 'Validity', fields: ['effective_from', 'effective_to'] }
        ]
      }
    };
    
    return configs[category] || { 
      icon: '⚙️', 
      color: 'gray', 
      sections: [{ name: 'Details', fields: fields.map(f => f.name) }]
    };
  };

  // Enhanced layout for all forms
  const renderEnhancedForm = () => {
    const config = getCategoryConfig(category);
    const colorClasses = {
      blue: 'bg-blue-50 border-blue-200',
      green: 'bg-green-50 border-green-200',
      purple: 'bg-purple-50 border-purple-200',
      indigo: 'bg-indigo-50 border-indigo-200',
      cyan: 'bg-cyan-50 border-cyan-200',
      teal: 'bg-teal-50 border-teal-200',
      emerald: 'bg-emerald-50 border-emerald-200',
      orange: 'bg-orange-50 border-orange-200',
      red: 'bg-red-50 border-red-200',
      gray: 'bg-gray-50 border-gray-200'
    };
    
    return (
      <Modal
        isOpen={true}
        onClose={onClose}
        title={item ? `Edit ${title}` : `Add New ${title}`}
        size="xl"
        theme="admin"
        footer={
          <>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-admin-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="config-form"
              disabled={loading}
              className="btn btn-admin-primary"
            >
              {loading ? 'Saving...' : (item ? 'Update' : 'Create')}
            </button>
          </>
        }
      >
          {/* Department-specific notice */}
          {category === 'departments' && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-blue-900 mb-1">Basic Department Setup</h4>
                  <p className="text-sm text-blue-800">
                    This form is for basic department creation and metadata. For advanced features like assigning Heads of Department, 
                    employee management, analytics, and organizational hierarchy, visit{' '}
                    <button
                      type="button"
                      onClick={() => window.open('/departments', '_blank')}
                      className="font-medium underline hover:text-blue-900 transition-colors"
                    >
                      Department Management
                    </button>
                    {' '}page.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          <form id="config-form" onSubmit={handleSubmit} className="space-y-6">
            {config.sections.map((section, index) => {
              const sectionFields = section.fields.map(fieldName => 
                fields.find(f => f.name === fieldName)
              ).filter(Boolean);
              
              if (sectionFields.length === 0) return null;
              
              // Skip hidden sections
              if (section.hidden) return null;
              
              const isCollapsed = collapsedSections.has(section.name);
              const toggleCollapse = () => {
                setCollapsedSections(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(section.name)) {
                    newSet.delete(section.name);
                  } else {
                    newSet.add(section.name);
                  }
                  return newSet;
                });
              };

              return (
                <div key={section.name} className={`${colorClasses[config.color]} rounded-xl p-4 border ${section.collapsible ? 'border-dashed' : ''}`}>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className={`text-sm font-semibold text-gray-700 flex items-center ${section.collapsible ? 'text-blue-700' : ''}`}>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {section.name}
                      {section.collapsible && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Advanced</span>
                      )}
                    </h4>
                    {section.collapsible && (
                      <button
                        type="button"
                        onClick={toggleCollapse}
                        className="text-red-600 hover:text-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded transition-colors"
                        aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isCollapsed ? "M19 9l-7 7-7-7" : "M5 15l7-7 7 7"} />
                        </svg>
                      </button>
                    )}
                  </div>
                  {!isCollapsed && (
                    <div className={`grid gap-4 ${sectionFields.length > 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                      {sectionFields.map(field => (
                        <div key={field.name} className={field.type === 'textarea' || field.type === 'calculation_method_select' ? 'md:col-span-2' : ''}>
                          <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center space-x-2">
                              <span>
                                {field.label}
                                {field.required && <span className="text-red-500 ml-1">*</span>}
                              </span>
                              <Tooltip content={getFieldHelp(category, field.name)}>
                                <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </Tooltip>
                            </div>
                          </label>
                          {renderField(field)}
                          {validationErrors[field.name] && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors[field.name]}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </form>
      </Modal>
    );
  };

  return category === 'workScheduleTemplates' ? renderWorkScheduleTemplateForm() : renderEnhancedForm();
};

export default ConfigForm; 